import { MenuLevelConst, type MenuLevel } from "@/contexts/menuLevel";

/**
 * Detail routes the OWN menu alone declares, keyed by the list they hang under.
 *
 * A record shared down from above is opened at own level with its OWNER in
 * `?org=` — a foreign org, which `OrgContext`'s `getLevelFromSearch` would
 * otherwise read as
 * CROSS, flipping the sidebar out from under a page that is own level by
 * construction. Splitting the route was meant to stop that, but the own menu
 * gates its items on `atOwnLevel`, so the path was only registered once the
 * level was already OWN: the route could never establish the level it was
 * carrying. Hence this list — the ROUTE decides, and `?org=` is left free to
 * name the owner (which is still what `scopeOrgId` resolves to).
 *
 * Only for entities whose shared rows are actually CLICKABLE at own level. Where
 * the list keeps a shared row out of the detail (site maps, geolayers, BI
 * dashboards, the image library — plain name, actions gated on `isManaged`), no
 * link ever names a foreign owner, one route serves both levels and nothing
 * belongs here.
 *
 * Three readers, and they must agree: `useOwnLevelMenu` / `useCrossLevelMenu`
 * take their `subPages` from it, `OrgContext` resolves the level from it, and
 * every row link picks its route through `detailRouteFor`.
 */
export const OWN_LEVEL_DETAIL_ROUTES: Record<
  string,
  { own: string; cross: string }
> = {
  // The three naming conventions below are all pre-existing; they are listed
  // rather than derived because no rule connects `_update` / `_admin_update`,
  // `_manage` / `_manage_admin` and `_update` / `_update_admin`.
  "/role_manager": { own: "/role_manager_update", cross: "/role_manager_admin_update" },
  "/device_usage_profile": {
    own: "/device_usage_profile_manage",
    cross: "/device_usage_profile_manage_admin",
  },
  // Geozones share down through Area.visibleForChildOrgs
  // (AreaServiceImpl.getAccessibleOrganisations) and the list links EVERY row,
  // so a shared geozone opens at own level with its owner in `?org=`.
  "/areas": { own: "/areas_update", cross: "/areas_update_admin" },
};

const OWN_LEVEL_PATHS = new Set(
  Object.values(OWN_LEVEL_DETAIL_ROUTES).map((r) => r.own)
);

/**
 * The detail route to open a row on, for a list that has a pair.
 *
 * Callers pass the level they are rendering at, so the ROUTE carries it and
 * `?org=` is left to name the record's owner. Reading the pair from here rather
 * than from a ternary per list is what keeps the menus, the level derivation and
 * the links from drifting apart — they all read this one map.
 */
export const detailRouteFor = (listPath: string, level: MenuLevel): string => {
  const pair = OWN_LEVEL_DETAIL_ROUTES[listPath];
  if (!pair) return listPath;
  return level === MenuLevelConst.OWN ? pair.own : pair.cross;
};

/**
 * True when the path itself says "own level", whatever `?org=` carries.
 *
 * Trailing slash tolerated: these routes are declared `exact: false`, so the
 * router matches `/x/` too and an exact lookup would fail silently — which for
 * this rule means an empty sidebar, not an error.
 */
export const isOwnLevelOnlyPath = (pathname: string): boolean =>
  OWN_LEVEL_PATHS.has(pathname.replace(/\/+$/, "") || "/");
